/**
 * The geometry every control in the rail shares.
 *
 * The rail holds three kinds of thing — navigation links, a theme radiogroup, and menu
 * triggers — and each was built with its own metrics: 40px rounded squares for navigation, a
 * 36px circle for notifications, and 28px circles carrying 15px icons inside a ringed capsule
 * for the theme picker. None was wrong on its own. Together they read as a column of widgets
 * borrowed from different screens, and the eye cannot tell which of them are the same *kind*
 * of thing.
 *
 * One row shape, one selected treatment. Import these rather than retyping the classes, so
 * the next control added to the rail cannot drift.
 *
 * `railItem` is the ROW: always full rail width, in both states. It never resizes itself —
 * `railItemFill` below is what draws the square-or-pill highlight, as a separate layer, and
 * that split is deliberate. An earlier version had the row itself toggle `w-10` ↔ `w-full` so
 * its own background would read as a square when collapsed. That fought the rail's own width
 * transition: two elements' `width` were mid-flight on the same frames, `w-full` kept
 * re-resolving against a container that was itself still moving, and the combination visibly
 * reversed direction partway through — the icon drifted right, then back left, instead of
 * one continuous move. A row that is simply always 100% wide has nothing to fight: a
 * percentage width with no separate `width` transition on the same element just tracks its
 * (also-animating) parent for free, every frame, with no drift possible.
 *
 * What the row DOES transition is `padding-left`, between two fixed pixel numbers that don't
 * reference the rail's width at all — collapsed, it centres the icon in the 52px content box
 * inside the 76px rail (`(52 − 20) / 2 = 16px`, `avatarSized` uses AccountMenu's 28px avatar:
 * `(52 − 28) / 2 = 12px`); expanded, an icon settles to a 10px inset and the avatar to 6px, so
 * both are centred on the same line and their labels start at the same x. Two fixed endpoints, nothing else moving on this element — the same reasoning that
 * keeps `railItemFill` drift-free below.
 */
export function railItem(collapsed: boolean, avatarSized = false): string {
  // Expanded, the 28px avatar sits 4px further left than a 20px icon's inset, so the two share a
  // centre line down the rail rather than a left edge — a wider glyph left-aligned reads as offset.
  const inset = collapsed ? (avatarSized ? 'pl-3' : 'pl-4') : avatarSized ? 'pl-1.5' : 'pl-2.5';
  // `short:` — 34px rather than 40 on a short window (see styles.css). Fourteen places plus the
  // chrome do not fit a laptop's height at 40, and a rail that scrolls hides sections, which is
  // worse than rows six pixels tighter. Still comfortably above a pointer's target size.
  return `group relative flex h-10 short:h-[34px] w-full shrink-0 cursor-pointer items-center rounded-md ${inset} pr-2.5 transition-[padding-left] duration-[var(--duration)] ease-[var(--ease-out)]`;
}

/**
 * The visible hover/active box, painted on a layer BEHIND the icon and label rather than on
 * the row itself. The row is always the rail's full width (see `railItem` above), so a
 * background painted directly on it would always be a rectangle, never a square. This one is
 * sized with its own fixed-pixel inset transition instead: `6px` a side collapsed leaves a
 * 40px square inside the 52px collapsed row (`52 − 6 − 6`, matching the row's own maths);
 * `0` a side expanded lets it fill the row into the familiar full-width pill.
 *
 * Absolutely positioned, so it needs `relative` (not just default static flow) on this row's
 * icon and label to paint above it — both carry that separately.
 */
export function railItemFill(collapsed: boolean): string {
  // On a short window the row is 34px, so the collapsed inset grows to 9px to keep the fill square.
  const inset = collapsed
    ? 'left-1.5 right-1.5 short:left-[9px] short:right-[9px]'
    : 'left-0 right-0';
  return `pointer-events-none absolute inset-y-0 rounded-md transition-[left,right] duration-[var(--duration)] ease-[var(--ease-out)] ${inset}`;
}

/** Not the current page, not the chosen option — the row's text. */
export const RAIL_ITEM_IDLE_TEXT = 'text-ink-muted hover:text-ink';

/** Companion to `RAIL_ITEM_IDLE_TEXT`, for `railItemFill`. `group-hover` because the row, not
 *  this layer, is what's actually hovered. */
export const RAIL_ITEM_IDLE_BG = 'group-hover:bg-surface-sunk';

/**
 * The current page, or the chosen theme — the row's text.
 *
 * Deliberately the same treatment either case: to a reader, "the section you are in" and "the
 * theme you are on" are the same statement, and marking them differently invents a
 * distinction the user does not have.
 */
export const RAIL_ITEM_ACTIVE_TEXT = 'text-accent';

/** Companion to `RAIL_ITEM_ACTIVE_TEXT`, for `railItemFill`. */
export const RAIL_ITEM_ACTIVE_BG = 'bg-accent-soft';

/** A popover trigger's open state — the row's text. Radix sets `data-state` on the row
 *  itself, so this reads directly rather than through `group-`. */
export const RAIL_ITEM_OPEN_TEXT = 'data-[state=open]:text-ink';

/** Companion to `RAIL_ITEM_OPEN_TEXT`, for `railItemFill`. */
export const RAIL_ITEM_OPEN_BG = 'group-data-[state=open]:bg-surface-sunk';

/** Built but not reachable yet — visible, inert, and obviously not a destination. No fill:
 *  nothing here is ever hovered or active, so there is nothing for one to show. */
export const RAIL_ITEM_INERT = 'cursor-default! text-ink-subtle/60';

/** The size every rail icon is drawn at. */
export const RAIL_ICON = 20;

/**
 * A rail item's label, beside its icon.
 *
 * Always rendered — collapsing it via `max-width` and `opacity` rather than unmounting is
 * what makes it animatable at all; a conditionally-rendered span has nothing to transition
 * between and can only pop in and out. `truncate` doubles as the clip: at every instant of
 * the transition it hides whatever doesn't yet fit inside the current (moving) max-width, so
 * the text can never spill out past the rail while it is still resizing.
 *
 * The gap to the icon lives here, as `margin-left`, rather than as `gap` on the parent row.
 * A flex `gap` still reserves its space between two rendered children even when one is
 * `max-w-0` — an invisible label would leave a phantom 8px next to the icon inside the
 * collapsed 40px square, pushing it off-centre. Animating the margin away with the label
 * removes it cleanly instead.
 *
 * `emphasis` is for the one label bolder than the rest — the account trigger's name.
 * `avatarSized` matches `railItem`'s: the label beside the account avatar.
 */
export function railLabel(collapsed: boolean, emphasis = false, avatarSized = false): string {
  const weight = emphasis ? 'font-semibold' : 'font-medium';
  // Beside the avatar the gap is 4px, so its label starts where every icon row's label does:
  // 6px inset + 28px avatar + 4px = 10px inset + 20px icon + 8px.
  const gap = avatarSized ? 'ml-1' : 'ml-2';
  const state = collapsed ? 'ml-0 max-w-0 opacity-0' : `${gap} max-w-[10rem] opacity-100`;
  return `relative min-w-0 flex-1 truncate text-left text-[0.8125rem] ${weight} transition-[opacity,max-width,margin-left] duration-[var(--duration)] ease-[var(--ease-out)] ${state}`;
}

// ── The phone ────────────────────────────────────────────────────────────────
// Below `md` the rail is not a rail: it lies along the bottom edge as a tab bar, and its
// overflow lives in a sheet. Both are stated here for the same reason the rail's geometry is
// — so the next control added to either cannot invent its own metrics.

/** The size a bottom-bar icon is drawn at. Larger than the rail's 20px: it is the only thing
 *  in the tab carrying meaning at a glance, above a 10px label read at arm's length. */
export const TAB_ICON = 22;

/**
 * One tab in the bottom bar.
 *
 * `flex-1 basis-0` is what makes five tabs share the width evenly whatever their labels say —
 * the rail's `w-full` rows were never meant for a row layout, and five of them side by side
 * each demanding 100% is what produced the clipped bar this replaces.
 *
 * `min-h-14` clears the 44px touch minimum (DESIGN_SYSTEM.md §9) with room for the label
 * under the icon.
 *
 * The selected tab has no fill of its own — `MobileNav` draws one under it, so that it can move.
 */
export function tabItem(active: boolean): string {
  const state = active ? 'text-accent' : 'text-ink-muted hover:text-ink';
  // A capsule rather than the rail's 12px radius: the bar around it is a pill now, and a
  // rounded rectangle nested inside a stadium reads as a mistake at this size.
  return `relative flex min-h-14 min-w-0 flex-1 basis-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-pill px-0.5 py-1.5 transition-colors duration-[var(--duration-slow)] ${state}`;
}

/** The word under a tab's icon. Truncates rather than wraps: two lines would make one tab
 *  taller than its neighbours and shunt the whole bar's baseline. */
export const TAB_LABEL =
  'max-w-full truncate text-[0.625rem] font-medium leading-none tracking-[-0.005em]';

/**
 * A row in the More sheet.
 *
 * The rail's own row is 40px, which is right for a pointer and under the 44px a thumb needs,
 * so the sheet takes the same shape at `h-11` rather than reusing `railItem` at the wrong
 * size. Everything else — radius, the `bg-accent-soft` selected treatment — is the rail's, so
 * the sheet reads as the rail unfolded rather than as a second menu system.
 */
export function sheetItem(active = false): string {
  const state = active
    ? 'bg-accent-soft text-accent font-semibold'
    : 'text-ink hover:bg-surface-sunk';
  return `flex h-11 w-full shrink-0 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-left text-[0.875rem] font-medium disabled:opacity-50 ${state}`;
}
